export const TABLE_COLUMN_PREFERENCES_VERSION = 1;
export const TABLE_COLUMN_MIN_WIDTH = 96;
export const TABLE_COLUMN_MAX_WIDTH = 480;

export interface TableColumnPreferences {
  order: string[];
  hidden: string[];
  widths: Record<string, number>;
}

interface StoredTableColumnPreferences extends TableColumnPreferences {
  version: number;
}

export function defaultTableColumnPreferences(
  columnIds: readonly string[],
  defaultHiddenColumnIds: readonly string[] = [],
): TableColumnPreferences {
  const known = new Set(columnIds);
  return {
    order: [...columnIds],
    hidden: defaultHiddenColumnIds.filter((id, index, ids) => known.has(id) && ids.indexOf(id) === index),
    widths: {},
  };
}

export function clampTableColumnWidth(width: number, min = TABLE_COLUMN_MIN_WIDTH): number {
  return Math.min(TABLE_COLUMN_MAX_WIDTH, Math.max(min, Math.round(width)));
}

export function parseStoredTableColumnPreferences(
  raw: string | null,
  columnIds: readonly string[],
  lockedColumnIds: ReadonlySet<string>,
  defaultHiddenColumnIds: readonly string[] = [],
): TableColumnPreferences {
  const defaults = defaultTableColumnPreferences(columnIds, defaultHiddenColumnIds);
  if (!raw) return defaults;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredTableColumnPreferences>;
    if (parsed.version !== TABLE_COLUMN_PREFERENCES_VERSION) return defaults;
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) return defaults;
    if (!parsed.widths || typeof parsed.widths !== "object" || Array.isArray(parsed.widths)) return defaults;

    const known = new Set(columnIds);
    const seen = new Set<string>();
    const order = parsed.order.filter((id): id is string => {
      if (typeof id !== "string" || !known.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const missing = columnIds.filter((id) => !seen.has(id));
    for (const id of missing) order.push(id);

    const hidden = parsed.hidden.filter(
      (id): id is string => typeof id === "string" && known.has(id) && !lockedColumnIds.has(id),
    );
    for (const id of defaultHiddenColumnIds) {
      if (
        missing.includes(id) &&
        known.has(id) &&
        !lockedColumnIds.has(id) &&
        !parsed.hidden.includes(id)
      ) {
        hidden.push(id);
      }
    }
    const widths: Record<string, number> = {};
    for (const [id, width] of Object.entries(parsed.widths)) {
      if (known.has(id) && typeof width === "number" && Number.isFinite(width)) {
        widths[id] = clampTableColumnWidth(width, id === "actions" ? 64 : TABLE_COLUMN_MIN_WIDTH);
      }
    }

    return { order, hidden: [...new Set(hidden)], widths };
  } catch {
    return defaults;
  }
}

export function serializeTableColumnPreferences(preferences: TableColumnPreferences): string {
  return JSON.stringify({ version: TABLE_COLUMN_PREFERENCES_VERSION, ...preferences });
}

export function tableColumnStorageKey(tableId: string, userId: string): string {
  return `bridge-atlas:table-columns:v${TABLE_COLUMN_PREFERENCES_VERSION}:${encodeURIComponent(userId)}:${tableId}`;
}
